import { useState, useCallback, useMemo } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import { useAttachmentUpload, UploadedAttachmentVersion } from './useAttachmentUpload';
import { AttachmentUploadBatchError } from './AttachmentUploadBatchError';
import { useAttachmentDownload } from './useAttachmentDownload';
import { createAttachmentLink, createAttachmentLinks, FileDetail as CreateAttachmentLinkFileDetail } from '../../services/createAttachmentLinkService';
import { revokeAttachmentLink } from '../../services/revokeAttachmentLinkService';
import { publishAttachmentVersion } from '../../services/publishAttachmentVersion';
import { removeAttachmentVersion } from '../../services/removeAttachmentVersion';
import { ArtifactAttachments, AttachmentVersion } from '#types/System.types';
import type { LabelId } from '#types';
import { useEntityRecordScope } from '../../store';
import { useCurrentArtifact } from '../useCurrentArtifact';
import { sendEmail, SendEmailRequest } from '../../services/sendEmail';
import { Timestamp } from 'firebase/firestore';

/**
 * Configuration props for the useAttachments hook
 */
interface UseAttachmentsProps {
  /** Map of attachment IDs to their version IDs */
  value?: ArtifactAttachments;
  /** Callback function triggered when attachments change */
  onChange?: (value: ArtifactAttachments) => void;
  /** Callback for handling error messages */
  onError?: (error: Error) => void;
  /** Callback for handling success messages */
  onSuccess?: (message: string) => void;
}

/**
 * Type of operation being performed
 */
type OperationType = 'upload' | 'download' | 'remove' | 'share' | 'email' | 'rename' | 'publish' | null;

/**
 * Hook for managing file attachments with Firebase
 * 
 * Handles the core file reference functionality including:
 * - Uploading files with reference tracking
 * - Downloading files and specific versions
 * - Removing files and their references
 * - Creating and revoking share links
 * - Publishing a save as a published version
 * - Emailing file attachments
 * 
 * @param props Configuration options for the hook
 * @returns Object containing file data and core file operations
 */
export const useAttachments = ({
  value = {},
  onError,
  onSuccess,
  onChange
}: UseAttachmentsProps) => {
  const artifact = useCurrentArtifact();
  const insideFormContext = artifact.insideFormContext;
  const insideEntityContext = artifact.insideEntityContext;
  const formId = artifact.kind === 'form' ? artifact.formId : undefined;
  const versionId = artifact.kind === 'form' ? artifact.versionId : undefined;
  const scope = useEntityRecordScope();
  const selectedEntity = scope.entity.data;
  const selectedEntityRecord = scope.entityRecord.data;
  
  const [currentOperation, setCurrentOperation] = useState<OperationType>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const fileList = useMemo(() => {
    return Object.entries(value).map(([fileId, fileDataObject]) => {
      const { fileName, versions, currentVersionId } = fileDataObject;

      if (!versions || Object.keys(versions).length === 0) {
        return {
          uid: fileId,
          name: fileName || 'Unknown File (no versions)',
          status: 'done' as const,
          url: fileId,
          versions: {} as { [versionId: string]: AttachmentVersion },
          size: 0,
        } as UploadFile & {
            versions: { [versionId: string]: AttachmentVersion };
            size?: number;
          };
      }

      let latestVersion: AttachmentVersion | null = null;

      if (currentVersionId && versions[currentVersionId]) {
        latestVersion = versions[currentVersionId];
      } else if (versions && Object.keys(versions).length > 0) {
        let maxVersionNumber = -1;
        Object.values(versions).forEach(version => {
          if (version.versionNumber > maxVersionNumber) {
            maxVersionNumber = version.versionNumber;
            latestVersion = version;
          }
        });
      }

      if (!latestVersion && versions && Object.keys(versions).length > 0) {
        latestVersion = Object.values(versions)[0]; // Fallback to the first version if still not found
      }
      
      const displayName = fileName || (latestVersion ? latestVersion.versionFileName : 'Unnamed file');

      return {
        uid: fileId,
        name: displayName,
        status: 'done' as const,
        url: fileId,
        versions: versions as { [versionId: string]: AttachmentVersion },
        size: latestVersion?.sizeBytes,
      } as UploadFile & {
        versions: { [versionId: string]: AttachmentVersion };
        size?: number;
      };
    });
  }, [value]);
  
  const { 
    uploadAttachments, 
    isUploading
  } = useAttachmentUpload();
  
  const {
    downloadAttachment,
    cancelDownload,
    progress: downloadProgress,
    isDownloading
  } = useAttachmentDownload();
  
  /**
   * The panel-wide percentage, which no longer has an `upload` case: sending
   * bytes reports itself per file through `attachmentTransfersStore`, and a
   * single number across a batch told the user nothing about any file in it.
   * `remove` reports the same indeterminate half as the other single-call
   * operations (SYD-2478): removal is now one callable, so there is no
   * per-file count to divide and a real percentage would be a fiction.
   */
  const progress = useMemo(() => {
    switch (currentOperation) {
      case 'download':
        return downloadProgress;
      case 'remove':
        return isRemoving ? 50 : 0;
      case 'share':
        return isSharing ? 50 : 0;
      case 'email':
        return downloadProgress;
      case 'rename':
        return isRenaming ? 50 : 0;
      case 'publish':
        return isPublishing ? 50 : 0;
      default:
        return 0;
    }
  }, [currentOperation, downloadProgress, isRemoving, isSharing, isRenaming, isPublishing]);

  const formVersionPath =
    insideFormContext && formId && versionId
      ? `forms/${formId}/formVersions/${versionId}`
      : '';
    
  const entityRecordPath = 
    insideEntityContext && selectedEntity?.docId && selectedEntityRecord?.docId
      ? `entities/${selectedEntity.docId}/records/${selectedEntityRecord.docId}` 
      : '';
    
  const artifactType: 'entity' | 'form' = 
    insideEntityContext ? 'entity' : 'form';
  
  const artifactId =
    insideEntityContext
      ? selectedEntity?.docId || ''
      : versionId || '';
    
  const artifactPath = useMemo(() => 
    artifactType === 'form' ? formVersionPath : entityRecordPath,
    [artifactType, formVersionPath, entityRecordPath]
  );
  
  /**
   * Uploads multiple files to storage and creates records
   *
   * A file that failed is reported on its own transfer row, which stays up
   * carrying the reason and a Retry, so `AttachmentUploadBatchError` raises no
   * toast: the reader has already been told, next to the thing they can act on.
   * Every other failure happens before any row exists and still goes to
   * `onError`. The success toast counts the versions that committed, not the
   * files handed in, because a batch every file of which was cancelled resolves
   * empty and must not claim it uploaded them.
   *
   * @param files Array of files to upload
   * @param emailRecordId Optional email record id for attachment provenance
   * @param options Pass `suppressSuccessToast: true` when the caller shows its own success message (e.g. send email flow)
   * @returns Array of upload results with file metadata
   */
  const uploadFiles = useCallback(async (
    files: File[],
    emailRecordId?: string,
    options?: { suppressSuccessToast?: boolean },
  ): Promise<UploadedAttachmentVersion[]> => {
    if (files.length === 0) return [];

    try {
      setCurrentOperation('upload');

      const existingFiles = fileList.map(file => ({
        name: file.name,
        fileUid: file.uid,
        currentVersionId: value[file.uid]?.currentVersionId ?? null
      }));

      const results = await uploadAttachments(
        files,
        artifactPath,
        artifactType,
        artifactId,
        existingFiles,
        emailRecordId
      );
      
      if (!options?.suppressSuccessToast && results.length > 0) {
        onSuccess?.(`Uploaded ${results.length} file(s) successfully`);
      }
      
      return results;
    } catch (error) {
      console.error('Upload failed:', error);
      if (!(error instanceof AttachmentUploadBatchError)) {
        onError?.(error instanceof Error ? error : new Error('Upload failed'));
      }
      return [];
    } finally {
      setCurrentOperation(null);
    }
  }, [
    fileList, value, artifactPath, artifactType, artifactId,
    uploadAttachments, onSuccess, onError
  ]);

  /**
   * Adds one file to an attachment that already exists, as its next version.
   *
   * The row is bound to the file by object identity, not by name, because the
   * pointer decided which row takes the version and two rows may carry the same
   * name. `uploadFileName` is the name the panel rendered, and it is what the
   * reservation is made under — which is the only name that lasts, because
   * commit copies it into the version's `versionFileName` and the attachment
   * document's `name`. An entry predating SYD-2457 holds no top-level
   * `fileName`, so sending the dropped file's name would make it authoritative
   * and rename the row for good.
   *
   * No attachment-count check runs here, deliberately. The server refuses a
   * reservation at `MAX_ATTACHMENTS_PER_DOCUMENT` only when it adds an
   * attachment, and this one never does.
   *
   * @param fileUid The attachment the version belongs to.
   * @param file The dropped file, whose bytes and content type are its own.
   * @param uploadFileName The row's name as the panel resolved it, never a display alias.
   * @returns The committed version, or an empty array when nothing committed.
   */
  const uploadFileVersion = useCallback(async (
    fileUid: string,
    file: File,
    uploadFileName: string,
  ): Promise<UploadedAttachmentVersion[]> => {
    if (!value[fileUid]) return [];

    try {
      setCurrentOperation('upload');

      const results = await uploadAttachments(
        [file],
        artifactPath,
        artifactType,
        artifactId,
        [{
          name: uploadFileName,
          fileUid,
          currentVersionId: value[fileUid]?.currentVersionId ?? null,
          matchesFile: file,
          uploadFileName,
        }],
      );

      if (results.length > 0) {
        onSuccess?.(`Added a new version of "${uploadFileName}"`);
      }

      return results;
    } catch (error) {
      console.error('Version upload failed:', error);
      if (!(error instanceof AttachmentUploadBatchError)) {
        onError?.(error instanceof Error ? error : new Error('Upload failed'));
      }
      return [];
    } finally {
      setCurrentOperation(null);
    }
  }, [
    value, artifactPath, artifactType, artifactId,
    uploadAttachments, onSuccess, onError
  ]);
  
  /**
   * Downloads a file to the user's device
   *
   * `downloadAttachment` reports a failed read as `{ success: false, error }`
   * rather than throwing, and a chunked version fails for reasons the user needs
   * to read — a poisoned chunk, a missing chunk, a file past the browser
   * assembly ceiling. So the result is inspected and `error.message`, which the
   * chunked reader writes to be shown as-is, goes to `onError` untouched.
   *
   * A cancel is the one unsuccessful result that reaches neither callback. The
   * hook has no neutral channel, and the user who stopped the download already
   * knows they stopped it, so it returns quietly rather than announcing it.
   *
   * @param fileUid ID of the attachment document
   * @param fileName Name to use for the downloaded file
   * @returns Object containing success status and file information
   */
  const downloadFile = useCallback(async (fileUid: string, fileName: string) => {
    try {
      setCurrentOperation('download');

      const result = await downloadAttachment({
        fileUid,
        fileName
      });

      if (result.cancelled) {
        return result;
      }

      if (!result.success) {
        onError?.(result.error ?? new Error(`Failed to download "${fileName}"`));
        return result;
      }

      onSuccess?.(`Downloaded "${fileName}" successfully`);
      return result;
    } catch (error) {
      console.error('Download failed:', error);
      onError?.(error instanceof Error ? error : new Error('Download failed'));
      throw error;
    } finally {
      setCurrentOperation(null);
    }
  }, [downloadAttachment, onSuccess, onError]);
  
  /**
   * Downloads a specific version of a file
   *
   * Reports the read's own verdict: a failed chunked assembly reaches the user as
   * its own message, never as a success toast for a file that never arrived. A
   * cancel is silent — neither a message nor an error.
   *
   * The artifact map is not consulted to decide whether the read may run:
   * `downloadAttachment` resolves the version out of `attachments/{fileUid}`
   * itself, which is the document holding the desktop saves the version strip
   * lists and the map does not. The map is read only for a version number to put
   * in the message, and the message drops it when the map has none (SYD-2455).
   *
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to download
   * @param fileName The file name
   * @returns Promise that resolves when the file is downloaded
   */
  const downloadVersion = useCallback(async (
    fileUid: string,
    versionId: string,
    fileName: string
  ) => {
    const versionNumber = value[fileUid]?.versions?.[versionId]?.versionNumber;
    const versionLabel = versionNumber === undefined
      ? `"${fileName}"`
      : `version ${versionNumber} of ${fileName}`;

    try {
      setCurrentOperation('download');

      const result = await downloadAttachment({
        fileUid,
        versionId,
        fileName
      });

      if (result.cancelled) {
        return;
      }

      if (!result.success) {
        onError?.(
          result.error ?? new Error(`Failed to download ${versionLabel}`)
        );
        return;
      }

      onSuccess?.(`Downloaded ${versionLabel} successfully`);
    } catch (error) {
      console.error('Failed to download version:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to download ${versionLabel}`));
    } finally {
      setCurrentOperation(null);
    }
  }, [value, downloadAttachment, onSuccess, onError]);
  
  /**
   * Removes every version of a file from the current artifact.
   *
   * `removeAttachmentVersion` with a null `versionId` prunes this artifact from
   * each version in one transaction, deletes the versions that lose their last
   * reference, and deletes `attachments/{fileUid}` once none remains.
   *
   * Like `removeVersion` and `publishVersion`, this deliberately does not write
   * the attachments map back through `onChange`: the map it would write is the
   * panel's render, so a whole-map write resurrects entries the server has
   * pruned and erases versions a concurrent commit has landed — the SYD-2478
   * defect. The panel picks the removal up through the artifact document's live
   * subscription.
   *
   * @param fileUid ID of the attachment document
   * @param fileName Name of the file (for success message)
   * @returns Whether the removal succeeded
   */
  const removeFile = useCallback(async (fileUid: string, fileName: string) => {
    if (!artifactPath) {
      const error = new Error('Cannot remove file: No artifact path available from context');
      onError?.(error);
      throw error;
    }

    try {
      setCurrentOperation('remove');
      setIsRemoving(true);

      await removeAttachmentVersion({ fileUid, artifactPath, versionId: null });

      onSuccess?.(`File ${fileName} removed successfully`);

      return { success: true };
    } catch (error) {
      console.error('File deletion failed:', error);
      onError?.(error instanceof Error ? error : new Error('Delete failed'));
      throw error;
    } finally {
      setIsRemoving(false);
      setCurrentOperation(null);
    }
  }, [artifactPath, onSuccess, onError]);

  /**
   * Removes a specific version of a file.
   *
   * `removeAttachmentVersion` prunes this artifact's back reference from the
   * version it reads inside its own transaction, so a version the strip lists
   * from `attachments/{fileUid}` alone is removable exactly like one the artifact
   * map holds (SYD-2455). Only the file has to be in the map — the guard below
   * is a local courtesy that gives the user the better message and saves a round
   * trip; the callable enforces the same rule server-side.
   *
   * `versionNumber` is read off `value` before the call, because it is only
   * wanted for the message and `value` after an await may describe a different
   * form than the one the user acted on.
   *
   * This deliberately does not write the attachments map back through
   * `onChange` — see `removeFile`.
   *
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to remove
   * @param fileName The file name (for display/messaging)
   * @returns Promise that resolves when the version is removed
   */
  const removeVersion = useCallback(async (
    fileUid: string,
    versionId: string,
    fileName: string
  ) => {
    try {
      setCurrentOperation('remove');
      setIsRemoving(true);

      if (!value[fileUid]) {
        throw new Error(`This document does not hold ${fileName}, so no version of it can be removed.`);
      }

      const versionNumber = value[fileUid].versions?.[versionId]?.versionNumber;

      await removeAttachmentVersion({ fileUid, artifactPath, versionId });

      onSuccess?.(versionNumber === undefined
        ? `A version of ${fileName} was removed successfully`
        : `Version ${versionNumber} of ${fileName} removed successfully`);
    } catch (error) {
      console.error('Failed to remove version:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to remove version of ${fileName}`));
    } finally {
      setIsRemoving(false);
      setCurrentOperation(null);
    }
  }, [value, artifactPath, onSuccess, onError]);

  /**
   * Publishes a file version, promoting a desktop-sync save into a published
   * version. One-way and permanent: the callable stamps `kind` and nothing
   * turns a published version back into a save.
   *
   * The panel picks the new kind up through the artifact document's live
   * subscription; this deliberately does not write the attachments map back
   * through `onChange`, because a whole-map write can resurrect entries the
   * server has already pruned.
   *
   * The artifact-map guard is real here and stays: `publishAttachmentVersion`
   * refuses any version that is not the artifact's `currentVersionId`, and that
   * pointer only ever names a version the map holds.
   *
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to publish; must be the artifact's current version
   * @param fileName The file name (for display/messaging)
   * @returns The publish result, or null when the call failed
   */
  const publishVersion = useCallback(async (
    fileUid: string,
    versionId: string,
    fileName: string
  ) => {
    try {
      setCurrentOperation('publish');
      setIsPublishing(true);

      if (!value[fileUid] || !value[fileUid].versions[versionId]) {
        throw new Error(`This document does not hold that version of ${fileName}, so it cannot be published.`);
      }

      const result = await publishAttachmentVersion({ fileUid, versionId, artifactPath });

      onSuccess?.(`"${fileName}" published as version ${result.versionNumber}`);

      return result;
    } catch (error) {
      console.error('Failed to publish version:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to publish ${fileName}`));
      return null;
    } finally {
      setIsPublishing(false);
      setCurrentOperation(null);
    }
  }, [value, artifactPath, onSuccess, onError]);

  /**
   * Creates a shareable link for a file version
   *
   * The artifact-map guard is real here and stays: a link is minted against the
   * artifact's own reference to the version, and a link needs one whole Storage
   * object, which the chunked saves the map does not hold never have.
   *
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to share
   * @param fileName The file name (for display/messaging)
   * @param expirationTime Optional expiration timestamp for the link
   * @returns Promise with the share link result including the download URL
   */
  const shareVersion = useCallback(async (
    fileUid: string,
    versionId: string,
    fileName: string,
    expirationTime?: number
  ) => {
    try {
      setCurrentOperation('share');
      setIsSharing(true);
      
      if (!value[fileUid] || !value[fileUid].versions[versionId]) {
        throw new Error(`This document does not hold that version of ${fileName}, so it cannot be shared.`);
      }
      
      const result = await createAttachmentLink(fileUid, versionId, artifactPath, expirationTime);
      
      if (!result.error) {
        onSuccess?.(expirationTime 
          ? `Share link created for "${fileName}" with expiration` 
          : `Share link created for "${fileName}"`
        );
      } else {
        throw new Error(result.error);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to create share link:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to create share link for ${fileName}`));
      return null;
    } finally {
      setIsSharing(false);
      setCurrentOperation(null);
    }
  }, [value, artifactPath, onSuccess, onError]);

  /**
   * Creates shareable links for multiple file versions in a single operation
   * 
   * @param files Array of file details to share
   * @param expirationTime Optional expiration timestamp for the links
   * @returns Promise with the batch share results
   */
  const shareMultipleVersions = useCallback(async (
    files: Array<{
      fileId: string;
      versionId: string;
      fileName: string;
      artifactPath?: string;
    }>,
    expirationTime?: number
  ) => {
    try {
      setCurrentOperation('share');
      setIsSharing(true);
      
      for (const file of files) {
        if (!value[file.fileId] || !value[file.fileId].versions[file.versionId]) {
          throw new Error(`Version not found for file: ${file.fileName}`);
        }
      }
      
      const fileDetails: CreateAttachmentLinkFileDetail[] = files.map(file => ({
        fileId: file.fileId,
        versionId: file.versionId,
        artifactPath: file.artifactPath || artifactPath
      }));
      
      const batchResult = await createAttachmentLinks(fileDetails, expirationTime);
      
      if (batchResult.error) {
        throw new Error(batchResult.error);
      }
      
      const successResults = Object.values(batchResult.results).filter(result => !result.error);
      const failureResults = Object.values(batchResult.results).filter(result => result.error);
      const successCount = successResults.length;
      const failureCount = failureResults.length;
      
      if (successCount > 0) {
        onSuccess?.(expirationTime 
          ? `Created share links for ${successCount} file(s) with expiration${failureCount > 0 ? ` (${failureCount} failed)` : ''}` 
          : `Created share links for ${successCount} file(s)${failureCount > 0 ? ` (${failureCount} failed)` : ''}`
        );
      } else if (failureCount > 0) {
        throw new Error(`Failed to create any share links. ${failureCount} file(s) failed.`);
      }
      
      return batchResult;
    } catch (error) {
      console.error('Failed to create share links:', error);
      onError?.(error instanceof Error ? error : new Error('Failed to create share links'));
      return null;
    } finally {
      setIsSharing(false);
      setCurrentOperation(null);
    }
  }, [value, artifactPath, onSuccess, onError]);

  /**
   * Revokes a shareable link for a file version
   * 
   * Updates the file's metadata and Firestore records to indicate
   * the file is no longer shared, invalidating previous access.
   * 
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to unshare
   * @param customArtifactPath Optional explicit artifact path to use, overrides the context path
   * @returns Promise with the revoke link result
   */
  const unshareVersion = useCallback(async (
    fileUid: string,
    versionId: string,
    customArtifactPath?: string
  ) => {
    try {
      setCurrentOperation('share');
      setIsSharing(true);
      
      if (!value[fileUid] || !value[fileUid].versions[versionId]) {
        throw new Error('This document does not hold that version, so its share link cannot be revoked here.');
      }
      
      const pathToUse = customArtifactPath || artifactPath;
      
      const result = await revokeAttachmentLink(fileUid, versionId, pathToUse);
      
      if (result.success) {
        onSuccess?.(`Share link revoked successfully`);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to revoke share link:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to revoke share link`));
      return null;
    } finally {
      setIsSharing(false);
      setCurrentOperation(null);
    }
  }, [value, artifactPath, onSuccess, onError]);

  /**
   * Prepares a file version for email and opens an email dialog
   *
   * The bytes come from the download funnel with `returnBlob`, so a chunked
   * version is assembled and verified in the browser before it is attached. A
   * failed read is rethrown carrying its own message, which is why `success` is
   * checked before the blob: "no blob" would otherwise replace "chunk 3 does not
   * match its digest". A cancel returns without an email and without an error.
   *
   * The artifact map is not consulted: the funnel resolves the version out of
   * `attachments/{fileUid}`, so a desktop save the strip lists and the map does
   * not hold emails exactly like any other version (SYD-2455).
   *
   * @param fileUid The ID of the attachment document
   * @param versionId The ID of the version to email
   * @param fileName The file name (for display/messaging)
   * @param emailTo Optional email recipient(s)
   * @param emailSubject Optional email subject
   * @param emailMessage Optional email message body
   * @returns Promise with the result of the email operation
   */
  const emailAttachment = useCallback(async (
    fileUid: string,
    versionId: string,
    fileName: string,
    emailTo?: string | string[],
    emailSubject?: string,
    emailMessage?: string
  ) => {
    try {
      setCurrentOperation('email');
      setIsEmailing(true);
      
      const downloadResult = await downloadAttachment({
        fileUid,
        versionId,
        fileName,
        returnBlob: true
      });

      if (downloadResult.cancelled) {
        return { success: false, cancelled: true };
      }

      if (!downloadResult.success) {
        throw downloadResult.error ?? new Error('Failed to prepare file for email');
      }

      if (!downloadResult.blob || !(downloadResult.blob instanceof Blob)) {
        throw new Error('Failed to prepare file for email');
      }

      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        if (!downloadResult.blob) {
          reject(new Error('No blob data available'));
          return;
        }
        reader.readAsDataURL(downloadResult.blob);
      });
      
      const base64Data = base64Content.split('base64,')[1];
      
      if (emailTo) {
        const emailRequest: SendEmailRequest = {
          to: emailTo,
          subject: emailSubject || `File: ${fileName}`,
          text: emailMessage || `Please find the attached file: ${fileName}`,
          html: (emailMessage || `Please find the attached file: ${fileName}`).replace(/\n/g, '<br/>'),
          attachments: [{
            filename: fileName,
            content: base64Data,
            contentType: downloadResult.contentType || 'application/octet-stream'
          }]
        };
        
        const result = await sendEmail(emailRequest);
        
        if (result.success) {
          onSuccess?.(`Email with "${fileName}" sent successfully`);
        }
        
        return { success: true, result };
      }
      
      return {
        success: true,
        fileName,
        base64Content: base64Data,
        contentType: downloadResult.contentType || 'application/octet-stream'
      };
    } catch (error) {
      console.error('Failed to prepare file for email:', error);
      onError?.(error instanceof Error ? error : new Error(`Failed to email ${fileName}`));
      return { success: false, error };
    } finally {
      setIsEmailing(false);
      setCurrentOperation(null);
    }
  }, [downloadAttachment, onSuccess, onError]);

  /**
   * Renames a file by updating its primary `fileName` property.
   * The `versionFileName` of individual historical versions is not affected.
   * This updates the `fileName` in the local state representation (`ArtifactAttachments`)
   * and calls the `onChange` callback. Persistence to Firestore is expected
   * to be handled by the consuming component via the `onChange` prop,
   * which should trigger an update of the artifact document.
   *
   * @param fileUid The ID of the attachment document (the file to rename)
   * @param newFileName The new file name to apply
   * @returns Promise that resolves when the renaming is processed
   */
  const renameFile = useCallback(async (
    fileUid: string,
    newFileName: string
  ) => {
    setCurrentOperation('rename');
    setIsRenaming(true);
    try {
      const existing = value[fileUid];
      if (!existing) {
        throw new Error('File not found for renaming.');
      }
      const trimmedNewFileName = newFileName.trim();
      if (trimmedNewFileName === '') {
        throw new Error('New file name cannot be empty.');
      }

      const updatedAttachments: ArtifactAttachments = {
        ...value,
        [fileUid]: {
          ...existing,
          fileName: trimmedNewFileName,
          meta: { ...existing.meta, lastModified: Timestamp.now() },
        },
      };

      onChange?.(updatedAttachments); 
      
      onSuccess?.(`File name updated to "${trimmedNewFileName}" successfully.`);
      
      return { success: true, updatedAttachments };

    } catch (error) {
      console.error('Failed to rename file:', error);
      onError?.(error instanceof Error ? error : new Error(String(error instanceof Error ? error.message : error)));
      return { success: false, error };
    } finally {
      setIsRenaming(false);
      setCurrentOperation(null);
    }
  }, [value, onChange, onSuccess, onError]);

  /**
   * Merges fields into one attachment entry and hands the whole next map to
   * `onChange`, which is the only shape the artifact write path accepts. Both
   * spreads matter: an implementation assigning the map loses every sibling
   * attachment, and one rebuilding the entry loses the fields it was not given.
   *
   * Nothing here can throw and nothing here can wait: the caller's write is
   * debounced by the persist layer, so it is neither attempted nor rejected
   * before this returns. A rejection surfaces on that layer's own error channel
   * and cannot be reported from here.
   *
   * @param fileUid The ID of the attachment document.
   * @param fields The fields to write onto that attachment.
   */
  const updateFileFields = useCallback(async (
    fileUid: string,
    fields: Partial<ArtifactAttachments[string]>,
  ) => {
    if (!onChange) return;

    onChange({
      ...value,
      [fileUid]: {
        ...(value[fileUid] || {}),
        ...fields,
      },
    } as ArtifactAttachments);
  }, [value, onChange]);

  /**
   * Updates the user notes for a specific attachment.
   *
   * @param fileUid The ID of the attachment document.
   * @param notes The new notes string.
   */
  const updateFileNotes = useCallback(
    (fileUid: string, notes: string) => updateFileFields(fileUid, { userNotes: notes }),
    [updateFileFields],
  );

  /**
   * Updates the importance for a specific attachment.
   *
   * @param fileUid The ID of the attachment document.
   * @param importance The new importance level.
   */
  const updateFileImportance = useCallback(
    (fileUid: string, importance: string) => updateFileFields(fileUid, { importance }),
    [updateFileFields],
  );

  /**
   * Replaces the labels assigned to one attachment on this artifact.
   *
   * @param fileUid The ID of the attachment document.
   * @param labelIds The complete next set of label ids for that attachment.
   */
  const updateFileLabels = useCallback(
    (fileUid: string, labelIds: LabelId[]) => updateFileFields(fileUid, { labelIds }),
    [updateFileFields],
  );

  return {
    fileList,
    attachments: value,
    uploadFiles,
    uploadFileVersion,
    downloadFile,
    downloadVersion,
    cancelDownload,
    removeFile,
    removeVersion,
    publishVersion,
    shareVersion,
    shareMultipleVersions,
    unshareVersion,
    emailAttachment,
    renameFile,
    updateFileNotes,
    updateFileImportance,
    updateFileLabels,
    progress,
    isUploading,
    isDownloading,
    isRemoving,
    isSharing,
    isEmailing,
    isRenaming,
    isPublishing,
    currentOperation,
    artifactPath
  };
}; 